import type { UserData, Context, Component } from '../api/models/types'

import { cloneDeep, isEqual } from 'lodash'
import { Controller } from '@hotwired/stimulus'
import { connectUserData, getFlow } from '../helpers/controllerHelpers/utils'
import { hasUrlParam, isBuilderEditingMode } from '../helpers/utils'
import { handleRequest } from '../api/handleRequest'
import { getBaseBuilderVersion } from '../api/helpers/utils'

export default class extends Controller {
  static values = {
    repeaterKey: String,
    repeaterValue: Array,
    url: String,
    pageId: String,
    componentId: String,
    component: Object,
  }

  declare readonly repeaterKeyValue: string
  declare repeaterValueValue: unknown[] | undefined
  declare readonly componentIdValue: string
  declare readonly componentValue: Component
  declare readonly pageIdValue: string
  disconnectUserData: (() => void) | undefined
  urlValue: string | null | undefined
  flowIdValue: string | null | undefined
  fetchIndex: number
  declare groupIdValue: string
  declare userDataValue: UserData
  declare html: string
  declare isEditingValue: boolean | null | undefined
  containerObserver: MutationObserver

  connect() {
    const flowEl = this.element.closest(`[data-controller*="flow"]`)
    this.flowIdValue = flowEl?.getAttribute('data-flow-id-value')
    this.urlValue = flowEl?.getAttribute('data-flow-url-value')
    this.isEditingValue =
      this.element.getAttribute('data-editing-mode-component-selected') === 'true' ||
      this.element.querySelector('[data-editing-mode-component-selected="true"]')
        ? true
        : false

    this.disconnectUserData = connectUserData(this, this.userDataValueChanged)
    this.userDataValueChanged()

    this.connectIsEditingValueObserver()
  }

  disconnect() {
    if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
    if (this.containerObserver) this.containerObserver.disconnect()
  }

  connectIsEditingValueObserver() {
    if (this.componentValue.type === 'Container') {
      this.containerObserver = new MutationObserver((mutationsList) => {
        mutationsList.forEach((mutation) => {
          if (
            mutation.type === 'attributes' &&
            mutation.attributeName === 'data-editing-mode-component-selected'
          ) {
            const mutationTarget = mutation.target as Element
            const isTargetMainRepContainer =
              mutationTarget.getAttribute('data-component-key-value') === this.componentValue.key
            const isTargetSelected =
              mutationTarget.getAttribute('data-editing-mode-component-selected') === 'true'

            if (isTargetMainRepContainer && isTargetSelected && !this.isEditingValue) {
              //* If the component is selected, we set isEditingValue to true and update the component
              this.isEditingValue = true
              this.updateComponent()
            } else if (isTargetSelected && this.isEditingValue === true) {
              //* If the component is already selected, we do nothing
              return
            } else if (!isTargetSelected && this.isEditingValue === true) {
              //* I check if any of the components in the repeatable are selected, in that case I do nothing
              if (
                this.element.getAttribute('data-editing-mode-component-selected') === 'true' ||
                this.element.querySelector('[data-editing-mode-component-selected="true"]')
              )
                return

              //* If none of the components in the repeatable are selected, we set isEditingValue to false and update the component
              this.isEditingValue = false
              this.updateComponent({ forceUpdate: true })
            }
          }
        })
      })

      if (this.element) {
        this.containerObserver.observe(this.element, {
          attributes: true,
          attributeFilter: ['data-editing-mode-component-selected'],
          childList: true, // Observe changes in the child elements
          subtree: true, // Observe changes in the entire subtree
        })
      }
    }
  }

  repeaterKeyValueChanged() {
    this.userDataValue && this.updateComponent()
  }

  userDataValueChanged() {
    this.updateComponent()
  }

  updateComponent({ forceUpdate = false }: { forceUpdate?: boolean } = {}) {
    const repeaterValue = this.userDataValue[this.repeaterKeyValue] || []

    if (
      (!forceUpdate &&
        isEqual(this.repeaterValueValue, repeaterValue) &&
        !(Array.isArray(repeaterValue) && repeaterValue.length === 0) &&
        !this.isEditingValue &&
        (this.element.innerHTML === this.html || !this.componentValue.always_rerender)) ||
      !this.urlValue ||
      !this.pageIdValue ||
      !Array.isArray(repeaterValue)
    )
      return

    this.repeaterValueValue = repeaterValue

    const isGlobalComponent = this.element.getAttribute('data-global-component') === 'true'
    let url = `${this.urlValue}/page/${isGlobalComponent ? 'global' : this.pageIdValue}/component/${
      this.componentIdValue
    }`

    if (isBuilderEditingMode() && !hasUrlParam('savvy_editing', 'no_sidebar'))
      url += '?savvy_editing=true'

    this.fetchIndex = (this.fetchIndex || 0) + 1
    const fetchIndex = this.fetchIndex

    let childComponents: Component[] = []
    if (this.componentValue.type === 'Container') {
      const allComponentsInSameLocation = isGlobalComponent
        ? (getFlow(this, true) || []).components || []
        : ((getFlow(this, true) || []).pages || []).find((p) => p.id === this.pageIdValue)
            ?.components || []

      // Recursively add all child components to childComponents

      const getChildComponents = (parent: Component) => {
        //* In builder v4, the parent_key is deprecated and only the parent_id is used
        const builderVersion = getBaseBuilderVersion(getFlow(this, true)?.builder_version)
        const childComponents = allComponentsInSameLocation.filter((c) =>
          builderVersion > 3
            ? c.parent_id === parent.id
            : c.parent_key === parent.key || c.parent_id === parent.id
        )
        childComponents.forEach((c) => {
          childComponents.push(...getChildComponents(c))
        })
        return childComponents
      }

      childComponents = getChildComponents(this.componentValue)
    }

    const context: Context = {
      env: 'local',
      fetch: (...args: any[]) => fetch(args[0], args[1]),
      flowsBucket: {},
    }
    const requestURL = new URL(url)
    const headers = new Headers()

    let repeatables: Component[] = []

    if (!this.isEditingValue) {
      repeatables = (this.repeaterValueValue || []).map((c: any, index) => {
        let newComponent = {
          ...this.componentValue,
          ...(c || {}),
          id: getUniqueItemKey(this.componentIdValue, c.key),
          key: getUniqueItemKey(this.componentValue.key, c.key),
          is_repeatable_child: true,
        }
        if (childComponents.length > 0) {
          newComponent.components = childComponents.map((_c) => {
            const __c = cloneDeep(_c)

            __c.id = getUniqueItemKey(__c.id, c.key)
            __c.key = getUniqueItemKey(__c.key, c.key)
            __c.parent_key = getUniqueItemKey(__c.parent_key as string, c.key)
            __c.parent_id = getUniqueItemKey(__c.parent_id as string, c.key)
            __c.is_repeatable_child = true

            return __c
          })
        }
        return newComponent
      })
    } else {
      //* If this is editing value, I keep only the first instance of the repeatable with the original key and id
      const firstInstance: Component[] = (this.repeaterValueValue || []).slice(0, 1) as Component[]

      let newComponent = {
        ...this.componentValue,
        ...(firstInstance[0] || {}),
        id: this.componentIdValue,
        key: this.componentValue.key,
      }
      if (childComponents.length > 0) {
        newComponent.components = childComponents.map((_c) => {
          const __c = cloneDeep(_c)

          //* Remove the is_repeatable_child property from the components so they can be individually selected in the builder
          const { is_repeatable_child, ...rest } = __c

          return rest
        })
      }
      repeatables = [newComponent]
    }

    const flow = getFlow(this, true)
    const body = {
      json: {
        flowId: this.flowIdValue,
        groupId:
          this.element
            .closest('[data-flow-group-id-value]')
            ?.getAttribute('data-flow-group-id-value') || '',
        flow,
        repeatables: repeatables,
        page: {
          ...(flow?.pages.find((p) => p.id === this.userDataValue.current_page_id) || {}),
          key: this.userDataValue.current_page_key,
          id: this.userDataValue.current_page_id,
        },
      },
    }
    const request = {
      url: requestURL,
      method: 'POST',
      headers,
      body,
    }

    handleRequest(context, request)
      .then((res) => {
        return JSON.parse(res.responseData)
      })
      .then((componentHtmls) => {
        // this.element.innerHTML = componentHtmls.join('')
        if (fetchIndex !== this.fetchIndex) return

        const tempEls = componentHtmls.map((html, index) => {
          const tempEl = document.createElement('div')
          tempEl.innerHTML = html
          ;(tempEl?.firstChild as HTMLElement)?.setAttribute(
            'data-controller',
            (tempEl.firstChild as HTMLElement)
              ?.getAttribute('data-controller')
              ?.replace('repeatable', '')
              .trim() as string
          )
          // @TODO: Do this without storing the data in the DOM
          ;(tempEl?.firstChild as HTMLElement)?.setAttribute(
            'data-component-local-data-value',
            JSON.stringify(
              ((this.repeaterValueValue || [])?.[index] as { key: string; data: any })?.data || {}
            )
          )
          return tempEl
        })
        this.html = tempEls.map((tempEl) => tempEl.innerHTML).join('')
        this.element.innerHTML = this.html

        this.triggerOnRepeatableRenderActions()
      })
  }

  triggerOnRepeatableRenderActions() {
    //* I trigger onrender actions after updating the innerHTML of the element
    if (this.componentValue.outputs_onrepeatablerender) {
      const actions = this.componentValue.outputs_onrepeatablerender

      this.dispatch('actions', {
        detail: {
          actions: actions,
          component: this.componentValue,
        },
      })
    }
  }
}

function getUniqueItemKey(original_key: string, repeater_value: string) {
  return `${repeater_value}--${original_key}`
}
